import React, { createContext, useContext, ReactNode } from 'react';
import { useKeycloak } from '@react-keycloak/web';

export interface UserInfo {
  id: string;
  email: string;
  name: string;
  customerId?: string;
  role: 'admin' | 'user' | 'viewer';
}

interface AuthContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  token: string | undefined;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();

  // TEMPORARY: Mock user when Keycloak is not available
  const KEYCLOAK_ENABLED = true;

  const getUserInfo = (): UserInfo | null => {
    if (!KEYCLOAK_ENABLED) {
      // Return mock user for development
      return {
        id: 'dev-user',
        email: 'dev@timbertrack.de',
        name: 'Development User',
        customerId: '1',
        role: 'admin',
      };
    }

    if (!keycloak.authenticated || !keycloak.tokenParsed) {
      return null;
    }

    const token = keycloak.tokenParsed as {
      sub?: string;
      email?: string;
      name?: string;
      preferred_username?: string;
      customer_id?: string;
      realm_access?: {
        roles?: string[];
      };
      resource_access?: {
        [key: string]: {
          roles?: string[];
        };
      };
    };
    
    // Get realm roles
    const realmRoles = token.realm_access?.roles || [];
    
    // Get client roles from resource_access (timbertrack-api)
    const clientId = 'timbertrack-api';
    const clientRoles = token.resource_access?.[clientId]?.roles || [];
    
    // Combine both realm and client roles
    const allRoles = Array.from(new Set([...realmRoles, ...clientRoles]));
    
    const isAdmin = allRoles.includes('admin');
    const userRole = isAdmin ? 'admin' : (allRoles.includes('viewer') ? 'viewer' : 'user');
    
    return {
      id: token.sub || '',
      email: token.email || '',
      name: token.name || token.preferred_username || '',
      customerId: token.customer_id,
      role: userRole,
    };
  };

  const login = () => {
    if (KEYCLOAK_ENABLED) {
      keycloak.login();
    }
  };

  const logout = () => {
    if (KEYCLOAK_ENABLED) {
      keycloak.logout({ redirectUri: 'http://www.timbertrack.de' });
    }
  };

  // Check for admin role in both realm_access and resource_access (client roles)
  const isAdminValue = KEYCLOAK_ENABLED ? (() => {
    const realmRoles = keycloak.tokenParsed?.realm_access?.roles || [];
    const clientRoles = keycloak.tokenParsed?.resource_access?.['timbertrack-api']?.roles || [];
    const allRoles = [...realmRoles, ...clientRoles];
    return allRoles.includes('admin');
  })() : true;

  const value: AuthContextType = {
    user: getUserInfo(),
    isAuthenticated: KEYCLOAK_ENABLED ? (keycloak.authenticated || false) : true,
    isAdmin: isAdminValue,
    isLoading: KEYCLOAK_ENABLED ? !initialized : false,
    login,
    logout,
    token: KEYCLOAK_ENABLED ? keycloak.token : undefined,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
